'use strict';

import AppDispatcher from '../AppDispatcher';
import ActionTypes from '../constants/ActionTypes';
import AnalyticsSources from '../constants/AnalyticsSources';
import ZineAPI from '../api/ZineAPI';
import ZineStore from '../stores/ZineStore';
import ZineSectionStore from '../stores/ZineSectionStore';
import ZineSectionsStore from '../stores/ZineSectionsStore';
import SuggestedZineStore from '../stores/SuggestedZineStore';
import UserCuratedZineStore from '../stores/UserCuratedZineStore';
import UserCuratedUnlistedZineStore from '../stores/UserCuratedUnlistedZineStore';
import UserContributedZineStore from '../stores/UserContributedZineStore';
import UserContributedUnlistedZineStore from '../stores/UserContributedUnlistedZineStore';
import SearchZineStore from '../stores/SearchZineStore';
import ActionUtils from '../utils/ActionUtils';
import handleUnauthAction from '../utils/handleUnauthAction';

var ZineActionCreators = {
  requestZine(zineSlug, fields) {
    ActionUtils.requestEntity({
      zineSlug: zineSlug,
      idAttribute: 'zineSlug',
      store: ZineStore,
      action: ActionTypes.REQUEST_ZINE,
      fields: fields,
      callAPI: ZineAPI.requestZine
    });
  },

  followZine(zineSlug) {
    if (handleUnauthAction(AnalyticsSources.FOLLOW_ZINE)) {
      return;
    }

    AppDispatcher.handleViewAction({
      type: ActionTypes.FOLLOW_ZINE,
      zineSlug: zineSlug
    });

    ZineAPI.followZine(zineSlug);
  },

  unfollowZine(zineSlug) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.UNFOLLOW_ZINE,
      zineSlug: zineSlug
    });

    ZineAPI.unfollowZine(zineSlug);
  },

  submitStampToZine(zineSlug, stampSegment) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.SUBMIT_STAMP_TO_ZINE,
      zineSlug: zineSlug,
      stampSegment: stampSegment
    });

    ZineAPI.submitStampToZine(zineSlug, stampSegment);
  },

  addStampToZine(zineSlug, stampSegment) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.ADD_STAMP_TO_ZINE,
      zineSlug: zineSlug,
      stampSegment: stampSegment
    });

    ZineAPI.addStampToZine(zineSlug, stampSegment);
  },

  removeStampFromZine(zineSlug, stampSegment) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.REMOVE_STAMP_FROM_ZINE,
      zineSlug: zineSlug,
      stampSegment: stampSegment
    });

    ZineAPI.removeStampFromZine(zineSlug, stampSegment);
  },

  approveStampToZine(zineSlug, stampSegment, message) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.APPROVE_STAMP_TO_ZINE,
      zineSlug: zineSlug,
      stampSegment: stampSegment,
      message: message
    });

    ZineAPI.approveStampToZine(zineSlug, stampSegment, message);
  },

  rejectStampFromZine(zineSlug, stampSegment, message) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.REJECT_STAMP_FROM_ZINE,
      zineSlug: zineSlug,
      stampSegment: stampSegment,
      message: message
    });

    ZineAPI.rejectStampFromZine(zineSlug, stampSegment, message);
  },

  createNewZineDraft(draft) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CREATE_ZINE_DRAFT,
      isNew: true,
      zine: draft
    });
  },

  createZineDraftFromZine(zine) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CREATE_ZINE_DRAFT,
      zine: zine
    });
  },

  updateZineDraft(key, value) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.UPDATE_ZINE_DRAFT,
      key: key,
      value: value
    });
  },

  destroyZineDraft() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DESTROY_ZINE_DRAFT
    });
  },

  createZine(zine) {
    ActionUtils.postEntity({
      entity: zine,
      action: ActionTypes.CREATE_ZINE,
      callAPI: ZineAPI.createZine
    });
  },

  updateZine(zine) {
    ActionUtils.updateEntity({
      entity: zine,
      action: ActionTypes.UPDATE_ZINE,
      callAPI: ZineAPI.updateZine
    });
  },

  deleteZine(zineSlug) {
    ActionUtils.deleteEntity({
      idAttribute: 'zineSlug',
      zineSlug: zineSlug,
      action: ActionTypes.DELETE_ZINE,
      callAPI: ZineAPI.deleteZine
    });
  },

  requestZinesInSection(sectionId, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      idAttribute: 'sectionId',
      sectionId: sectionId,
      isInitialRequest: isInitialRequest,
      store: ZineSectionStore,
      action: ActionTypes.REQUEST_ZINES_IN_SECTION,
      callAPI: ZineAPI.requestZinesInSection
    });
  },

  requestZineSections(isInitialRequest) {
    ActionUtils.requestPage({
      isInitialRequest: isInitialRequest,
      store: ZineSectionsStore,
      action: ActionTypes.REQUEST_ZINE_SECTIONS,
      callAPI: ZineAPI.requestZineSections
    });
  },

  requestSuggestedPage(isInitialRequest) {
    ActionUtils.requestPage({
      isInitialRequest: isInitialRequest,
      store: SuggestedZineStore,
      action: ActionTypes.REQUEST_SUGGESTED_ZINES,
      callAPI: ZineAPI.requestSuggestedPage
    });
  },

  requestUserCuratedPage(userId, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserCuratedZineStore,
      action: ActionTypes.REQUEST_USER_CURATED_ZINES,
      callAPI: ZineAPI.requestUserCuratedPage
    });
  },

  requestUserCuratedUnlistedPage(userId, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserCuratedUnlistedZineStore,
      action: ActionTypes.REQUEST_USER_CURATED_UNLISTED_ZINES,
      callAPI: ZineAPI.requestUserCuratedUnlistedPage
    });
  },

  requestUserContributedPage(userId, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserContributedZineStore,
      action: ActionTypes.REQUEST_USER_CONTRIBUTED_ZINES,
      callAPI: ZineAPI.requestUserContributedPage
    });
  },

  requestUserContributedUnlistedPage(userId, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserContributedUnlistedZineStore,
      action: ActionTypes.REQUEST_USER_CONTRIBUTED_UNLISTED_ZINES,
      callAPI: ZineAPI.requestUserContributedUnlistedPage
    });
  },

  requestSearchPage(terms, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      terms: terms,
      idAttribute: 'terms',
      isInitialRequest: isInitialRequest,
      store: SearchZineStore,
      action: ActionTypes.REQUEST_ZINE_SEARCH,
      callAPI: ZineAPI.requestSearchPage
    });
  }
};

module.exports = ZineActionCreators;



/** WEBPACK FOOTER **
 ** ./actions/ZineActionCreators.js
 **/